"use client";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { IframeDefaultLocationListener } from "@/components/iframe-default-location-listener/iframe-default-location-listener";
import {
  useIframeResizeReporter,
  useLocatorStackedLayout,
} from "@/components/iframe-resize-reporter/iframe-resize-reporter";
import { LocationCard } from "../../components/location-card/index";
import {
  MapComponent,
  MapComponentHandle,
  INDIVIDUAL_MARKER_ZOOM,
  type MapCenterChange,
} from "../../components/map/index";
import {
  SearchField,
  type GeoSearchQuery,
} from "../../components/search-field/search-field";
import {
  fetchLocations,
  filterLocationsByCoordinates,
  filterLocationsInBounds,
  sortLocationsByDistance,
  type ILocation,
} from "../../services/locationApi";
import type { LocationCluster } from "@/lib/stateClusters";

function NavigationIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0"
    >
      <polygon points="3 11 22 2 13 21 11 13 3 11" />
    </svg>
  );
}

function LocatorMap({
  accessToken,
  initialLocations,
}: {
  accessToken: string;
  initialLocations?: ILocation[];
}) {
  const hasProvidedInitialLocations = initialLocations !== undefined;
  const initial = initialLocations || [];
  const [allLocations, setAllLocations] = useState<ILocation[]>(initial);
  const [listLocations, setListLocations] = useState<ILocation[]>(initial);
  const [loading, setLoading] = useState(!hasProvidedInitialLocations);
  const [error, setError] = useState<string | null>(null);
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(
    null,
  );
  const [searchFieldKey, setSearchFieldKey] = useState(0);
  const [externalQuery, setExternalQuery] = useState<string | undefined>(
    undefined,
  );
  const [geoQuery, setGeoQuery] = useState<GeoSearchQuery | undefined>(
    undefined,
  );
  const [showMobileMap, setShowMobileMap] = useState(false);

  const mapRefMobile = useRef<MapComponentHandle>(null);
  const mapRefDesktop = useRef<MapComponentHandle>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const panDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isStackedLayout = useLocatorStackedLayout();

  useIframeResizeReporter({
    containerRef,
    enabled: isStackedLayout,
    contentDeps: [listLocations.length, loading, showMobileMap, error],
  });

  const invokeOnMapHandles = useCallback(
    (fn: (h: MapComponentHandle) => void) => {
      const m = mapRefMobile.current;
      const d = mapRefDesktop.current;
      if (m) fn(m);
      if (d) fn(d);
    },
    [],
  );

  useLayoutEffect(() => {
    if (!isStackedLayout || !showMobileMap) return;

    mapRefMobile.current?.resize();
    const rafId = requestAnimationFrame(() => mapRefMobile.current?.resize());
    return () => cancelAnimationFrame(rafId);
  }, [showMobileMap, isStackedLayout]);

  // Refs for scrolling selected card into view
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Sort: selected card always first, rest by distance
  const sortedLocations = useMemo(() => {
    if (!selectedLocationId) return listLocations;
    const selected = listLocations.find((l) => l.id === selectedLocationId);
    const rest = listLocations.filter((l) => l.id !== selectedLocationId);
    return selected ? [selected, ...rest] : listLocations;
  }, [listLocations, selectedLocationId]);

  // Scroll selected card into view when selection changes.
  // On mobile, also ask the parent page to scroll the iframe into view via postMessage.
  useEffect(() => {
    if (!selectedLocationId) return;
    window.parent.postMessage(
      { action: "anchor-search-bar", navbarOffset: 80 },
      "*",
    );
    cardRefs.current[selectedLocationId]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  }, [selectedLocationId]);

  useEffect(() => {
    if (hasProvidedInitialLocations) return;

    const loadLocations = async () => {
      try {
        setLoading(true);
        setError(null);
        const fetched = await fetchLocations();
        setAllLocations(fetched);
        setListLocations(fetched);
      } catch (err) {
        setError("Failed to load locations. Please try again later.");
        console.error("Error loading locations:", err);
      } finally {
        setLoading(false);
      }
    };

    loadLocations();
  }, [hasProvidedInitialLocations]);

  useEffect(() => {
    return () => {
      if (panDebounceRef.current) clearTimeout(panDebounceRef.current);
    };
  }, []);

  const handleMapCenterChange = useCallback(
    (view: MapCenterChange) => {
      if (panDebounceRef.current) clearTimeout(panDebounceRef.current);
      panDebounceRef.current = setTimeout(() => {
        setSelectedLocationId(null);
        setError(null);
        setGeoQuery(undefined);
        setExternalQuery(undefined);
        setSearchFieldKey((k) => k + 1);
        const nearby =
          view.zoom >= INDIVIDUAL_MARKER_ZOOM
            ? filterLocationsByCoordinates(allLocations, view.lat, view.lng)
            : filterLocationsInBounds(
                allLocations,
                view.bounds,
                view.lat,
                view.lng,
              );
        setListLocations(nearby);
        if (nearby.length === 0) {
          setError(
            view.zoom >= INDIVIDUAL_MARKER_ZOOM
              ? "No Broken Yolk Cafe locations found within 50 miles of this area"
              : "No Broken Yolk Cafe locations found in this map area",
          );
        }
      }, 300);
    },
    [allLocations],
  );

  const handleClusterSelect = useCallback((cluster: LocationCluster) => {
    setError(null);
    setGeoQuery(undefined);
    setExternalQuery(undefined);
    setSearchFieldKey((k) => k + 1);
    const sorted = sortLocationsByDistance(
      cluster.locations,
      cluster.lat,
      cluster.lng,
    );
    setListLocations(sorted);
    setSelectedLocationId(
      cluster.count === 1 ? cluster.locations[0].id : null,
    );
  }, []);

  const handleLocationSearch = useCallback(
    async (location: { name: string; lat: number; lng: number }) => {
      setSelectedLocationId(null);

      try {
        setLoading(true);
        setError(null);

        let catalog = allLocations;
        if (catalog.length === 0) {
          catalog = await fetchLocations();
          setAllLocations(catalog);
        }
        const nearby = filterLocationsByCoordinates(
          catalog,
          location.lat,
          location.lng,
        );
        setListLocations(nearby);

        const nearest = nearby[0];
        if (nearest && nearest.distance <= 10) {
          invokeOnMapHandles((h) =>
            h.flyTo(
              [nearest.fieldData.longitude, nearest.fieldData.latitude],
              14,
            ),
          );
        } else {
          invokeOnMapHandles((h) =>
            h.fitToSearchResult([location.lng, location.lat], nearby),
          );
        }

        if (nearby.length === 0) {
          setError(
            `No Broken Yolk Cafe locations found within 50 miles of ${location.name}`,
          );
        }
      } catch (err) {
        setError("Failed to search locations. Please try again.");
        console.error("Error searching locations:", err);
      } finally {
        setLoading(false);
      }
    },
    [allLocations, invokeOnMapHandles],
  );

  const handleSearchFieldSelect = useCallback(
    (location: { name: string; lat: number; lng: number }) => {
      setGeoQuery(undefined);
      setExternalQuery(undefined);
      handleLocationSearch(location);
    },
    [handleLocationSearch],
  );

  const handleClearSearch = async () => {
    setSelectedLocationId(null);
    setError(null);
    setGeoQuery(undefined);
    setExternalQuery(undefined);
    setSearchFieldKey((k) => k + 1);
    invokeOnMapHandles((h) => h.zoomOut());

    if (hasProvidedInitialLocations) {
      setAllLocations(initialLocations!);
      setListLocations(initialLocations!);
      return;
    }

    try {
      setLoading(true);
      const fetched = await fetchLocations();
      setAllLocations(fetched);
      setListLocations(fetched);
    } catch (err) {
      console.error("Error reloading locations:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleUseMyLocation = () => {
    setGeoQuery(undefined);
    window.parent.postMessage({ action: "request-geolocation" }, "*");
  };

  useEffect(() => {
    const handleGeoResponse = async (event: MessageEvent) => {
      const data = event.data;
      if (
        !data ||
        typeof data !== "object" ||
        data.action !== "geolocation-response" ||
        !data.coords
      )
        return;

      const { latitude: lat, longitude: lng } = data.coords as {
        latitude: number;
        longitude: number;
        accuracy: number;
      };

      try {
        const res = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${accessToken}&limit=1&types=place,address`,
        );
        const json = await res.json();
        const placeName: string | undefined = json.features?.[0]?.place_name;
        setExternalQuery(undefined);
        if (placeName) {
          setGeoQuery({ query: placeName, lat, lng });
        } else {
          setExternalQuery("your location");
          handleLocationSearch({ name: "your location", lat, lng });
        }
      } catch {
        setExternalQuery("your location");
        handleLocationSearch({ name: "your location", lat, lng });
      }
    };

    window.addEventListener("message", handleGeoResponse);
    return () => window.removeEventListener("message", handleGeoResponse);
  }, [accessToken, handleLocationSearch]);

  return (
    <div
      ref={containerRef}
      className="w-full max-lg:h-auto max-lg:py-6 lg:h-full lg:pt-[30px] lg:px-[40px] lg:pb-[64px] flex items-start min-h-0"
    >
      <IframeDefaultLocationListener
        onQuery={(query) => {
          setGeoQuery(undefined);
          setExternalQuery(query);
          setSelectedLocationId(null);
          setError(null);
        }}
      />
      <div className="lg:rounded-[40px] flex flex-col lg:flex-row flex-1 max-lg:overflow-visible lg:overflow-hidden min-h-0">
        {/* Map — top on mobile/tablet (always mounted; collapsed when hidden), hidden on desktop */}
        <div
          className={`w-full lg:hidden rounded-2xl overflow-hidden shrink-0 ${
            showMobileMap ? "h-[361px]" : "h-0 pointer-events-none"
          }`}
          aria-hidden={!showMobileMap}
        >
          <div className="h-full min-h-0">
            <MapComponent
              ref={mapRefMobile}
              locations={allLocations}
              accessToken={accessToken}
              initialZoom={2.8}
              selectedLocationId={selectedLocationId}
              onSelectLocation={setSelectedLocationId}
              onMapCenterChange={handleMapCenterChange}
              onClusterSelect={handleClusterSelect}
              showPopup={false}
            />
          </div>
        </div>

        {/* Sidebar */}
        <div
          className={`bg-white w-full lg:w-[420px] shrink-0 flex flex-col lg:h-[840px] min-h-0 ${showMobileMap ? "max-lg:mt-8" : ""} max-lg:rounded-2xl max-lg:overflow-visible lg:rounded-none lg:mt-0 lg:overflow-visible`}
        >
          {/* Header: search + use my location + count row */}
          <div className="border-b border-[#dbe1e5] px-6 py-5 lg:p-5 flex flex-col gap-[22px] shrink-0">
            <div className="flex flex-col gap-5">
              <SearchField
                key={searchFieldKey}
                accessToken={accessToken}
                onLocationSelect={handleSearchFieldSelect}
                externalQuery={externalQuery}
                geoQuery={geoQuery}
              />
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={handleUseMyLocation}
                  className="flex gap-2 items-center text-[#0b1c24] text-[14px] font-bold cursor-pointer"
                >
                  <NavigationIcon />
                  <span>Use my location</span>
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-[#6b767c] text-[16px] font-bold">
                {loading
                  ? "Loading…"
                  : `${listLocations.length} location(s) found nearby`}
              </span>
              <button
                type="button"
                onClick={() => setShowMobileMap((v) => !v)}
                className="lg:hidden flex gap-1.5 items-center text-[#0b1c24] text-[14px] font-bold cursor-pointer"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="shrink-0"
                >
                  {showMobileMap ? (
                    <>
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </>
                  ) : (
                    <>
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </>
                  )}
                </svg>
                <span>{showMobileMap ? "Hide Map" : "Show Map"}</span>
              </button>
            </div>
          </div>

          {/* Location list — grows with content on mobile/tablet; scrollable fixed panel on desktop */}
          <div className="max-lg:flex-none max-lg:overflow-visible lg:flex-1 lg:overflow-y-auto lg:min-h-0 lg:overscroll-contain">
            {!loading && (error || sortedLocations.length === 0) && (
              <div className="flex flex-col gap-4 items-center justify-center py-16 lg:h-full px-8 text-center">
                <div className="flex flex-col gap-2 items-center text-[#0b1c24] text-[16px]">
                  <p className="font-bold uppercase leading-[1.5] w-[262px]">
                    No Broken Yolk Cafe found near this location
                  </p>
                  <p className="leading-[1.5]">Try checking another ZIP/City</p>
                </div>
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="border-2 border-primary text-primary text-[14px] font-bold uppercase rounded-full px-6 py-[10px] transition-colors hover:bg-primary hover:text-white cursor-pointer"
                >
                  Clear Search
                </button>
              </div>
            )}

            {!loading &&
              !error &&
              sortedLocations.map((location) => (
                <LocationCard
                  key={location.id}
                  location={location}
                  isSelected={location.id === selectedLocationId}
                  onSelect={() => setSelectedLocationId(location.id)}
                  containerRef={(el) => {
                    cardRefs.current[location.id] = el;
                  }}
                />
              ))}
          </div>
        </div>

        {/* Map — hidden on mobile/tablet, visible on desktop */}
        <div className="hidden lg:block flex-1 min-w-0">
          <MapComponent
            ref={mapRefDesktop}
            locations={allLocations}
            accessToken={accessToken}
            selectedLocationId={selectedLocationId}
            onSelectLocation={setSelectedLocationId}
            onMapCenterChange={handleMapCenterChange}
            onClusterSelect={handleClusterSelect}
          />
        </div>
      </div>
    </div>
  );
}

export default LocatorMap;
